// ./src/components/generic/ContentTypeSelector/ContentTypeSelector.tsx
import React, { useEffect } from "react";
import { Select, SelectProps } from "@mantine/core";
import { AppIcon } from "@/components/generic";

const CONTENT_TYPE_OPTIONS = [
  {
    value: "IMAGE",
    label: "Image",
    icon: "Photo",
    description: "Image files (JPG, PNG, etc.)",
    disabled: false,
  },
  {
    value: "VIDEO",
    label: "Video",
    icon: "Video",
    description: "Video files (MP4, AVI, etc.)",
    disabled: false,
  },
  {
    value: "EXTERNAL_VIDEO",
    label: "External Video",
    icon: "Video",
    description: "Videos from YouTube, Vimeo, etc",
    disabled: false,
  },
  {
    value: "MESSAGE",
    label: "Message",
    icon: "Message",
    description: "Simple Message",
    disabled: false,
  },
  {
    value: "IFRAME",
    label: "IFrame",
    icon: "Link",
    description: "Display an HTML page by its URL",
    disabled: false,
  },
  {
    value: "HTML",
    label: "HTML",
    icon: "Code",
    description: "HTML markup content",
    disabled: false,
  },
  {
    value: "MARKDOWN",
    label: "Markdown",
    icon: "FileText",
    description: "Markdown formatted text",
    disabled: false,
  },
  {
    value: "CLOCK",
    label: "Clock",
    icon: "Clock",
    description: "Display current date and time",
    disabled: false,
  },
  {
    value: "CALENDAR",
    label: "Calendar",
    icon: "Calendar",
    description: "Display calendar for the current month",
    disabled: false,
  },
  {
    value: "COUNTDOWN",
    label: "Countdown",
    icon: "TrendingDown",
    description: "Countdown to provided date and time",
    disabled: false,
  },
  {
    value: "COUNTUP",
    label: "Countup",
    icon: "TrendingUp",
    description: "Countup from provided date and time",
    disabled: false,
  },
  {
    value: "UNSPLASH_COLLECTION",
    label: "Unsplash Collections",
    icon: "TrendingUp",
    description: "Showcase photos from an Unsplash collection",
    disabled: false,
  },
] as const;

export type ContentType = (typeof CONTENT_TYPE_OPTIONS)[number]["value"];

export interface ContentTypeSelectorProps
  extends Omit<SelectProps, "data" | "value" | "onChange"> {
  /** Current content type value */
  value: string | null;

  required?: boolean;

  /** Callback when content type changes */
  onChange: (value: string | null) => void;

  /** Whether to show icons in the dropdown */
  withIcons?: boolean;

  /** Whether to show descriptions in the dropdown */
  withDescriptions?: boolean;

  /** Whether to show "All types" option for filtering */
  showAllOption?: boolean;

  /** Label for the "All types" option */
  allOptionLabel?: string;
}

export const ContentTypeSelector: React.FC<ContentTypeSelectorProps> = ({
  value,
  onChange,
  required,
  withIcons = true,
  withDescriptions = false,
  showAllOption = false,
  allOptionLabel = "All types",
  placeholder = "Select content type",
  ...selectProps
}) => {
  // Prepare select data with optional icons and descriptions
  const selectData = React.useMemo(() => {
    const options: Array<{ value: string; label: string }> =
      CONTENT_TYPE_OPTIONS.map((option) => {
        let label: string = option.label;

        if (withDescriptions) {
          label = `${option.label} - ${option.description}`;
        }

        return {
          value: option.value,
          label,
        };
      });

    // Add "All types" option at the beginning if showAllOption is true
    if (showAllOption) {
      options.unshift({
        value: "",
        label: allOptionLabel,
      });
    }

    return options;
  }, [withDescriptions, showAllOption, allOptionLabel]);

  // Auto-select first content type if required and no value is set
  useEffect(() => {
    if (required && !value && onChange && !showAllOption) {
      const firstOption = CONTENT_TYPE_OPTIONS[0];
      if (firstOption && !firstOption.disabled) {
        onChange(firstOption.value);
      }
    }
  }, [required, value, onChange, showAllOption]);

  // Get current selection for display
  const currentOption = CONTENT_TYPE_OPTIONS.find((opt) => opt.value === value);
  const leftSection =
    withIcons && currentOption ? (
      <AppIcon iconName={currentOption.icon as any} size={16} />
    ) : withIcons && !value && showAllOption ? (
      <AppIcon iconName="Filter" size={16} />
    ) : undefined;

  return (
    <Select
      {...selectProps}
      data={selectData}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      leftSection={leftSection}
      searchable
      clearable={showAllOption && !required}
      size="md"
    />
  );
};

// Export the content type options for use in other components
export { CONTENT_TYPE_OPTIONS };
